import { Button } from "@/components/ui/button";
import { fmt } from "@/hooks/useVehicleSim";
import { ArrowLeft, ShoppingCart } from "lucide-react";

interface Props {
  vehicleName: string;
  effectivePrice: number;
  downPayment: number;
  apr: number;
  loanTermMonths: number;
  monthlyPayment: number;
  insuranceMonthly: number;
  maintenanceMonthly: number;
  fuelMonthly: number;
  totalMonthlyCost: number;
  firstYearCost: number;
  affordabilityRating: string | null;
  purchaseGrade: string | null;
  negotiationDiscount: number;
  onBuy: () => void;
  onExit: () => void;
  processing: boolean;
}

const GRADE_COLORS: Record<string, string> = {
  'A': 'bg-emerald-500/10 text-emerald-600',
  'A-': 'bg-emerald-500/10 text-emerald-600',
  'B+': 'bg-primary/10 text-primary',
  'B': 'bg-primary/10 text-primary',
  'C+': 'bg-amber-500/10 text-amber-600',
  'C': 'bg-amber-500/10 text-amber-600',
  'D': 'bg-destructive/10 text-destructive',
};

export default function FinalSummaryScreen({
  vehicleName, effectivePrice, downPayment, apr, loanTermMonths,
  monthlyPayment, insuranceMonthly, maintenanceMonthly, fuelMonthly,
  totalMonthlyCost, firstYearCost, affordabilityRating, purchaseGrade,
  negotiationDiscount, onBuy, onExit, processing,
}: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Final Purchase Summary</h2>
        <p className="text-sm text-muted-foreground">Review everything before making your decision.</p>
      </div>

      <div className="glass-card glow-border p-5 space-y-4">
        {purchaseGrade && (
          <div className="text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-heading font-bold ${GRADE_COLORS[purchaseGrade] || 'bg-muted text-muted-foreground'}`}>
              Purchase Grade: {purchaseGrade}
            </div>
          </div>
        )}

        <div className="text-center">
          <p className="font-heading font-bold text-foreground text-lg">{vehicleName}</p>
        </div>

        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Final Price</span><span className="font-semibold text-foreground">{fmt(effectivePrice)}</span></div>
          {negotiationDiscount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Negotiation Savings</span><span className="text-emerald-600 font-semibold">−{fmt(negotiationDiscount)}</span></div>}
          <div className="flex justify-between"><span className="text-muted-foreground">Down Payment</span><span className="text-foreground">{fmt(downPayment)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">APR</span><span className="text-foreground">{(apr * 100).toFixed(1)}%</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Loan Term</span><span className="text-foreground">{loanTermMonths} months</span></div>
        </div>

        <div className="border-t border-border pt-3 space-y-1.5 text-sm">
          <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Monthly Breakdown</p>
          <div className="flex justify-between"><span className="text-muted-foreground">Loan Payment</span><span className="text-foreground">{fmt(monthlyPayment)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Insurance</span><span className="text-foreground">{fmt(insuranceMonthly)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Maintenance</span><span className="text-foreground">{fmt(maintenanceMonthly)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Fuel</span><span className="text-foreground">{fmt(fuelMonthly)}</span></div>
          <div className="border-t border-border pt-1.5 flex justify-between font-semibold">
            <span className="text-foreground">Total Monthly</span>
            <span className="text-primary">{fmt(totalMonthlyCost)}</span>
          </div>
        </div>

        <div className="glass-card p-3 text-center">
          <p className="text-xs text-muted-foreground">First-Year Total Cost</p>
          <p className="text-xl font-heading font-bold text-foreground">{fmt(firstYearCost)}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Button onClick={onBuy} disabled={processing} className="w-full gap-2">
          <ShoppingCart className="w-4 h-4" /> {processing ? 'Processing...' : 'Buy Vehicle'}
        </Button>
        <Button variant="ghost" onClick={onExit} className="w-full text-muted-foreground">
          Walk Away
        </Button>
        <p className="text-[10px] text-center text-muted-foreground italic pt-1">
          This is the moment of decision. There is no going back to change earlier steps.
        </p>
      </div>
    </div>
  );
}
